import { computed, inject, Injectable, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';

export type PushFailureReason =
  | 'ios-not-installed'
  | 'permission-denied'
  | 'subscribe-failed'
  | 'unsubscribe-failed';

export type PushFailure = {
  readonly reason: PushFailureReason;
  readonly detail?: string;
};

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private notificationSubject = new Subject<Notification>();
  public notification$ = this.notificationSubject.asObservable();

  private swUpdate = inject(SwUpdate);
  private http = inject(HttpClient);
  private router = inject(Router);

  private readonly ctaDismissedStorageKey = 'push-cta-dismissed';
  private readonly subscribedSignal = signal(false);
  private readonly statusCheckedSignal = signal(false);
  private readonly ctaDismissedSignal = signal(
    localStorage.getItem(this.ctaDismissedStorageKey) === 'true',
  );

  readonly subscribed = this.subscribedSignal.asReadonly();
  private readonly failureSignal = signal<PushFailure | null>(null);
  readonly failure = this.failureSignal.asReadonly();
  readonly isSupported =
    'serviceWorker' in navigator && 'PushManager' in window;
  readonly shouldShowCta = computed(
    () =>
      this.statusCheckedSignal() &&
      !this.subscribedSignal() &&
      !this.ctaDismissedSignal(),
  );
  // VAPID public key (replace with your own)
  private vapidPublicKey =
    'BM9nJhrGD7CYA7VQddDJ1DA1PR8NecG806zDm0_OoJ2SW9ghf8GIPOlES_DQybANPF5zmkjgbuFnkBe9HsVGLvU';

  private isIosDevice(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  private isStandalonePwa(): boolean {
    const nav = navigator as Navigator & { standalone?: boolean };
    const standaloneByDisplayMode = window.matchMedia(
      '(display-mode: standalone)',
    ).matches;
    return standaloneByDisplayMode || nav.standalone === true;
  }

  constructor() {
    this.initializePushNotifications();
  }

  /**
   * Refresh the shared subscription state from the browser
   */
  async refreshStatus(): Promise<boolean> {
    const isSubscribed = await this.isSubscribed();
    this.subscribedSignal.set(isSubscribed);
    this.statusCheckedSignal.set(true);
    return isSubscribed;
  }

  /**
   * Hide the enable CTA until the user subscribes again
   */
  dismissCta(): void {
    localStorage.setItem(this.ctaDismissedStorageKey, 'true');
    this.ctaDismissedSignal.set(true);
  }

  private clearCtaDismissed(): void {
    localStorage.removeItem(this.ctaDismissedStorageKey);
    this.ctaDismissedSignal.set(false);
  }

  private describeError(error: unknown): string {
    return error instanceof Error
      ? `${error.name}: ${error.message}`
      : String(error);
  }

  /**
   * Initialize push notifications
   */
  private initializePushNotifications(): void {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(() => {
        this.handlePushMessages();
        this.handleNotificationClick();
      });
    }
  }

  /**
   * Request permission from user for notifications
   */
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribeToPush(): Promise<boolean> {
    this.failureSignal.set(null);

    try {
      const isIos = this.isIosDevice();
      const isStandalone = this.isStandalonePwa();

      console.log('📱 Push diagnostics:', {
        userAgent: navigator.userAgent,
        isIos,
        isStandalone,
        permission: Notification.permission,
      });

      if (isIos && !isStandalone) {
        console.warn(
          '⚠️ iOS requires installed Home Screen PWA for push notifications.',
        );
        this.failureSignal.set({ reason: 'ios-not-installed' });
        return false;
      }

      const permission = await this.requestNotificationPermission();
      if (!permission) {
        console.log('User denied notification permission');
        this.failureSignal.set({ reason: 'permission-denied' });
        return false;
      }

      const registration = await navigator.serviceWorker.ready;

      // Check if already subscribed
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey),
        });
      }

      // Send subscription to backend
      console.log('📤 Sending subscription to backend...');
      console.log('Subscription endpoint:', subscription.endpoint);
      try {
        await this.sendSubscriptionToBackend(subscription);
        console.log('✅ Subscription sent to backend successfully');
      } catch (error) {
        if (
          error instanceof HttpErrorResponse &&
          (error.status === 401 || error.status === 403)
        ) {
          console.warn(
            `⚠️ Push subscribe not allowed for current user (${error.status}).`,
          );
        } else {
          console.error('⚠️ Failed to send subscription to backend:', error);
          console.error('Error details:', error);
        }
        // Continue anyway - local subscription is active
      }

      console.log('Successfully subscribed to push notifications');
      this.subscribedSignal.set(true);
      this.clearCtaDismissed();
      return true;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      this.failureSignal.set({
        reason: 'subscribe-failed',
        detail: this.describeError(error),
      });
      if (error instanceof Error) {
        console.error('Subscribe error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
      }
      return false;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribeFromPush(): Promise<boolean> {
    this.failureSignal.set(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();

        // Notify backend
        try {
          await this.notifyBackendUnsubscribe(subscription);
          console.log('✅ Unsubscribe notified to backend');
        } catch (error) {
          console.error(
            '⚠️ Failed to notify backend about unsubscribe:',
            error,
          );
        }

        console.log('Successfully unsubscribed from push notifications');
        this.subscribedSignal.set(false);
        this.dismissCta();
        return true;
      }

      this.subscribedSignal.set(false);
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      this.failureSignal.set({
        reason: 'unsubscribe-failed',
        detail: this.describeError(error),
      });
      return false;
    }
  }

  /**
   * Check if user is subscribed to push notifications
   */
  async isSubscribed(): Promise<boolean> {
    console.log('🔍 Checking subscription status...');

    try {
      if (!('serviceWorker' in navigator)) {
        console.log('❌ Service Worker not supported');
        return false;
      }

      if (!('PushManager' in window)) {
        console.log('❌ Push Manager not supported');
        return false;
      }

      console.log('⏳ Waiting for service worker to be ready...');
      const registration = (await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Service Worker timeout')), 5000),
        ),
      ])) as ServiceWorkerRegistration;

      console.log('✅ Service Worker ready, checking subscription...');
      const subscription = await registration.pushManager.getSubscription();

      console.log('📋 Subscription status:', subscription !== null);
      if (subscription) {
        console.log('📍 Endpoint:', subscription.endpoint);
      }

      return subscription !== null;
    } catch (error) {
      console.error('❌ Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Sync existing subscription with backend
   */
  async syncSubscriptionWithBackend(): Promise<boolean> {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        console.log('📤 Syncing subscription with backend...');
        console.log('Subscription endpoint:', subscription.endpoint);

        try {
          await this.sendSubscriptionToBackend(subscription);
          console.log('✅ Subscription synced successfully');
          return true;
        } catch (error) {
          if (
            error instanceof HttpErrorResponse &&
            (error.status === 401 || error.status === 403)
          ) {
            console.warn(
              `⚠️ Push sync not allowed for current user (${error.status}).`,
            );
          } else {
            console.error('⚠️ Failed to sync subscription:', error);
          }
          return false;
        }
      } else {
        console.log('⚠️ No subscription found to sync');
        return false;
      }
    } catch (error) {
      console.error('❌ Error syncing subscription:', error);
      return false;
    }
  }

  /**
   * Listen for push messages in service worker
   */
  private handlePushMessages(): void {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'PUSH_RECEIVED') {
        this.notificationSubject.next(event.data.notification);
      }
    });
  }

  /**
   * Handle notification click actions
   */
  private handleNotificationClick(): void {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
        const action = event.data.action;
        // const action = event.action || event.data.action || event.notification.data?.action;

        console.log('🔔 Notification clicked with action:', action);
        this.handleAction(action);
      }
    });
  }

  /**
   * Handle notification actions and navigate accordingly
   */
  private handleAction(action: string): void {
    if (!action) return;

    // Parse action if it contains parameters
    const [actionType, ...params] = action.split(':');

    console.log('🎯 Handling action:', actionType, 'with params:', params);

    switch (actionType) {
      case 'navigate_to_alarms':
        this.router.navigate(['/alarms']);
        break;

      case 'navigate_to_location':
        if (params[0]) {
          this.router.navigate(['/locations', params[0], 'list']);
        } else {
          this.router.navigate(['/locations']);
        }
        break;

      case 'navigate_to_room':
        if (params[0] && params[1]) {
          this.router.navigate([
            '/locations',
            params[0],
            'room',
            params[1],
            'details',
          ]);
        }
        break;

      case 'open_url':
        if (params[0]) {
          window.open(params[0], '_blank');
        }
        break;

      default:
        console.warn('⚠️ Unknown action type:', actionType);
        // Default behavior - navigate to home/alarms
        this.router.navigate(['/alarms']);
        break;
    }
  }

  /**
   * Send subscription to backend
   */
  private async sendSubscriptionToBackend(
    subscription: PushSubscription,
  ): Promise<void> {
    const url = `${environment.apiBaseUrl}notifications/subscribe`;
    const subscriptionJson = subscription.toJSON();
    if (!subscriptionJson.keys) {
      throw new Error('Subscription keys are not available');
    }
    const payload = {
      endpoint: subscription.endpoint,
      p256dh: subscriptionJson.keys['p256dh'],
      auth: subscriptionJson.keys['auth'],
    };

    console.log('🌐 POST Request to:', url);
    console.log('📦 Payload:', payload);

    const response = await firstValueFrom(this.http.post(url, payload));

    console.log('📥 Response:', response);
  }

  /**
   * Notify backend on unsubscribe
   */
  private async notifyBackendUnsubscribe(
    subscription: PushSubscription,
  ): Promise<void> {
    const url = `${environment.apiBaseUrl}notifications/unsubscribe`;
    const payload = {
      endpoint: subscription.endpoint,
    };

    console.log('🌐 POST Request to:', url);
    console.log('📦 Payload:', payload);

    const response = await firstValueFrom(this.http.post(url, payload));

    console.log('📥 Response:', response);
  }

  /**
   * Convert VAPID key to ArrayBuffer
   */
  private urlBase64ToUint8Array(base64String: string): ArrayBuffer {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray.buffer;
  }
}
